/* @ts-nocheck */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DigitsNumAccordingToType, MfaType } from '../../../../generic-components/MFA/types';
import { useCountdownTimer } from '../../../../hooks/utilities/useCountdownTimer';
import { useValidateMfaCode } from '../../hooks/useValidateMfaCode';
import { useStore } from '../../../../store';
import { useResendMfaCode } from '../../hooks/useResendMfaCode';
import { useAccountSummaryQuery } from '../../../../hooks/query/useAccountSummaryQuery';
import { TemporaryBlocked } from '../../components/TemporaryBlocked';
import { trackMixPanel } from '../../../../services/mixpanel.service';
import { Layout } from '../../../../components/Layout/Layout';
import { IServerErrorResponse, RoleType } from '../../../../types';
import { InviteSuccess } from '../Success/InviteSuccess';
import { LiliLottieLoader } from '../../../../components/LiliLottieLoader/LiliLottieLoader';
import { InviteStepHeader } from '../../components/InviteStepHeader';
import { InviteMFAInput } from './components/InviteMFAInput';
import { InviteMFAResend } from './components/InviteMFAResend';
import { SERVER_ERRORS_DATA } from '../../../../constants/serverErrors';

interface IMfaResponse {
  mfaDestination: string;
  mfaType: MfaType;
  authorizedUserExternalId: string;
  accessToken: string;
}

interface LocationState {
  mfaData: IMfaResponse;
}

export const VerifyAccountantPhone = () => {
  const [resendType, setResendType] = useState<MfaType>(MfaType.SMS);
  const [isResendCode, setIsResendCode] = useState<boolean>(false);
  const [isFlowSucceed, setIsFlowSucceed] = useState<boolean>(false);
  const [isTempBlock, setIsTempBlock] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [roleType] = useState<RoleType>((searchParams.get('role') as RoleType) || RoleType.ACCOUNTANT);
  const location = useLocation();
  const { customerId } = useStore((state) => state.user);
  const state = location.state as LocationState | null;
  const { mfaData } = state || { mfaData: null };
  const numberOfDigits = useMemo(
    () => (resendType === MfaType.SMS ? DigitsNumAccordingToType.SMS : DigitsNumAccordingToType.VOICE),
    [resendType],
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { startTimer, timeLeft } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });
  const { mutateAsync: sendCode, isLoading } = useValidateMfaCode();
  const { mutateAsync: asyncResendCode, isLoading: isLoadingResend, data: resendMfaResponse } = useResendMfaCode();
  const { refetch: refetchSummaryForInvites, isLoading: isRefetchingSummary } = useAccountSummaryQuery();

  const handleSendError = (e: IServerErrorResponse) => {
    const tempLockCode = SERVER_ERRORS_DATA.USER_LOCKED_CODE;
    const error = e.response.data?.errors[0];
    if (tempLockCode === error?.code) {
      setIsTempBlock(true);
      setErrorMessage(undefined);
    } else {
      setErrorMessage(error?.message);
    }
  };
  const handleMfaCode = async (mfaCode: string) => {
    setErrorMessage(undefined);
    if (!mfaData?.mfaType || !mfaData.authorizedUserExternalId) return;

    try {
      await sendCode({
        customerId,
        mfaType: mfaData?.mfaType,
        answer: mfaCode,
        authorizedUserExternalId: mfaData?.authorizedUserExternalId,
      });
      await refetchSummaryForInvites();
      setIsFlowSucceed(true);
    } catch (e) {
      handleSendError(e as IServerErrorResponse);
    }
  };
  useEffect(() => {
    trackMixPanel('Page view', { pageName: 'Role invite MFA' });
  }, []);
  const handleResend = (mfaType: MfaType) => {
    setErrorMessage(undefined);
    asyncResendCode({ customerId, mfaType })
      .then(() => {
        setIsResendCode(false);
        startTimer();
      })
      .catch(handleSendError);
  };

  useEffect(() => {
    if (isFlowSucceed) {
      trackMixPanel('Page view', { pageName: 'Role invite success' });
    }
  }, [isFlowSucceed]);

  useEffect(() => {
    if (resendMfaResponse) {
      setResendType(resendMfaResponse.mfaType);
    }
  }, [resendMfaResponse]);

  const onMFAMethodClick = (mfaType: MfaType) => {
    setResendType(mfaType);
    handleResend(mfaType);
  };

  return (
    <Layout shouldHideProfile shouldHideChatBot wrapperClassName='absolute inset-0 z-[99] bg-bw02 '>
      <div className='flex flex-col items-center  '>
        <InviteStepHeader />
        <div className='mt-[128px]'>
          {!isResendCode && !isFlowSucceed && (
            <InviteMFAInput
              numberOfDigits={numberOfDigits}
              onResendClick={() => setIsResendCode(true)}
              handleMfaCode={handleMfaCode}
              isLoading={isLoading}
              timeLeft={timeLeft}
              mfaDestination={mfaData?.mfaDestination}
              errorMessage={errorMessage}
            />
          )}
          {isResendCode && !isFlowSucceed && <InviteMFAResend onMFAMethodClick={onMFAMethodClick} />}
        </div>
        {isFlowSucceed && <InviteSuccess role={roleType} />}
        {isTempBlock && <TemporaryBlocked onClose={() => setIsTempBlock(false)} />}
        {(isLoading || isLoadingResend || isRefetchingSummary) && <LiliLottieLoader />}
      </div>
    </Layout>
  );
};
